import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

// Shared across all "pill" variants; `surface` opts out by overriding the relevant utilities.
const PILL_BASE = "rounded-full px-2 py-0.5 text-3xs font-semibold uppercase tracking-wider";

/**
 * Semantic variants (success/warning/destructive/info) use the canonical `ds-*`
 * status tokens so they stay aligned with the Figma design system. Dark-mode
 * surfaces use a tinted alpha to preserve legibility without needing dedicated
 * dark tokens.
 */
const badgeVariants = cva("inline-flex items-center gap-1", {
  variants: {
    variant: {
      default: `${PILL_BASE} bg-ds-neutral-100 text-ds-text-subtle dark:bg-ds-neutral-800 dark:text-ds-text-disabled`,
      // Semantic — ds-backed
      success: `${PILL_BASE} bg-ds-success-surface text-ds-success-text dark:bg-green-500/10 dark:text-green-400`,
      destructive: `${PILL_BASE} bg-ds-error-surface text-ds-error-text dark:bg-red-500/10 dark:text-red-400`,
      warning: `${PILL_BASE} bg-ds-warning-surface text-ds-warning-text dark:bg-amber-500/10 dark:text-amber-400`,
      info: `${PILL_BASE} bg-ds-info-surface text-ds-info-text dark:bg-blue-500/10 dark:text-blue-400`,
      // Specialized one-offs kept on Tailwind scale
      "destructive-red": `${PILL_BASE} bg-red-600 text-white dark:bg-red-600 dark:text-white`,
      purple: `${PILL_BASE} bg-purple-50 text-purple-700 dark:bg-purple-500/10 dark:text-purple-400`,
      cyan: `${PILL_BASE} bg-cyan-50 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-400`,
      orange: `${PILL_BASE} bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-400`,
      b3: `${PILL_BASE} bg-ds-focus-brand text-b3-white`,
      outline: `${PILL_BASE} border border-ds-border-default text-ds-text-subtle dark:text-ds-text-disabled`,
      surface:
        "rounded-ds-xs border border-ds-neutral-300 bg-ds-surface-card px-1.5 py-0.5 text-xs font-medium normal-case tracking-normal text-ds-text-secondary shadow-ds-soft dark:border-ds-neutral-700",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  /** Show a colored dot before the label */
  dot?: boolean;
  /** Animate the dot (pulse) */
  dotPulse?: boolean;
}

function Badge({ className, variant, dot, dotPulse, children, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props}>
      {dot && (
        <span className={cn("h-1.5 w-1.5 rounded-full bg-current", dotPulse && "animate-pulse")} aria-hidden="true" />
      )}
      {children}
    </span>
  );
}

export { Badge, badgeVariants };
